import { useEffect, useRef, useState } from "react";
import AddProject from "../project/AddProject";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { useShowAlert } from "../../../context/AlertContext";
import { useNetwork } from "../../../context/NetworkContext";
import { AddIcon, RemoveIcon } from "../../../constants/Icons";

const ProjectSale = ({
  isActive,
  onSelectProject,
  resetProject,
  selectedProjectId,
}) => {
  const { checkNetwork } = useNetwork();
  const [projects, setProjects] = useState([]);
  const [projectData, setProjectData] = useState("");
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showProjectSuggestions, setShowProjectSuggestions] = useState(false);
  const [projectIndex, setProjectIndex] = useState(-1);
  const [isBtnLoading, setBtnLoading] = useState({
    list: false,
    add: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const projectRef = useRef(null);
  const projectInputRef = useRef(null);
  const projectItemRefs = useRef([]);
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const showAlert = useShowAlert();

  const searchProjects = (term) => {
    if (term.trim() === "") {
      setFilteredProjects([]);
      return;
    }

    // Clear previous debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Cancel previous request
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        if (!checkNetwork()) return;
        controllerRef.current = new AbortController();

        const currentId = ++requestIdRef.current;

        const response = await AxiosInstance.get(
          `module/project/search?searchtxt=${encodeURIComponent(term)}`,
          {
            signal: controllerRef.current.signal,
          },
        );

        if (currentId === requestIdRef.current) {
          setFilteredProjects(response?.data?.results || []);
          setProjectIndex(0);
        }
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          handleError(error, showAlert);
        }
      }
    }, 200);
  };

  const loadProjects = async () => {
    try {
      if (!checkNetwork()) return;

      const response = await AxiosInstance.get(
        "module/project/search?searchtxt=",
      );

      setFilteredProjects(response.data.results || []);
      setProjectIndex(0);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleSelectProject = (project) => {
    setProjectData(project);
    setProjectSearchTerm(project.name);
    setShowProjectSuggestions(false);

    onSelectProject?.(project._id);
  };

  const handleAddProject = () => {
    setNewProjectName(projectSearchTerm);
    setShowProjectSuggestions(false);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!showProjectSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setProjectIndex((prev) =>
          prev < filteredProjects.length - 1 ? prev + 1 : prev,
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setProjectIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;

      case "Enter":
        e.preventDefault();

        if (projectIndex >= 0 && filteredProjects[projectIndex]) {
          handleSelectProject(filteredProjects[projectIndex]);
        } else if (filteredProjects.length === 0 && projectSearchTerm.trim()) {
          handleAddProject();
        }
        break;

      case "Escape":
        setShowProjectSuggestions(false);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    setProjectData("");
    setProjectSearchTerm("");
    setShowProjectSuggestions(false);
  }, [resetProject]);

  useEffect(() => {
    const getProject = async () => {
      if (!selectedProjectId) {
        setProjectData("");
        setProjectSearchTerm("");
        return;
      }

      try {
        if (!checkNetwork()) return;
        const response = await AxiosInstance.get(
          `module/project/${selectedProjectId}`,
        );

        setProjectData(response.data.results);
      } catch (error) {
        console.error(error);
      }
    };

    getProject();
  }, [selectedProjectId]);

  useEffect(() => {
    if (projectIndex >= 0 && projectItemRefs.current[projectIndex]) {
      projectItemRefs.current[projectIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [projectIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectRef.current && !projectRef.current.contains(event.target)) {
        setProjectSearchTerm("");
        setShowProjectSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <div className="card">
        <div className="card-body">
          <div className="col-md-12">
            {/* <label className="form-label">Project</label> */}

            <div className="position-relative" ref={projectRef}>
              {projectData ? (
                <div className="card shadow-none border-blue mb-0">
                  <div className="card-header btn-label-project p-2 d-flex justify-content-between align-items-center">
                    <h8 className="mb-0">Project Info</h8>

                    <button
                      type="button"
                      className="btn-label-danger rounded shadow-none border-0 cursor-pointer"
                      onClick={() => {
                        setProjectData("");
                        setProjectSearchTerm("");
                        setFilteredProjects([]);
                        setShowProjectSuggestions(false);
                        onSelectProject?.(null);

                        setTimeout(() => {
                          projectInputRef.current?.focus();
                        }, 0);
                      }}
                    >
                      <RemoveIcon/>
                    </button>
                  </div>

                  <div className="card-body px-2 py-0">
                    <p className="my-2 fs-14">
                      <span className="fw-600 fs-14  ms-1">
                        {projectData.name}
                      </span>
                    </p>

                    {projectData.referenceid && (
                      <p className="my-2 fs-13">
                        Reference ID :
                        <span className="fw-600 fs-14 ms-1">
                          {projectData.referenceid}
                        </span>
                      </p>
                    )}

                    {projectData.description && (
                      <p className="my-2 fs-13">
                        Description :
                        <span className="fw-600 fs-14 ms-1">
                          {projectData.description}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <label className="form-label">Project</label>
                  <input
                    ref={projectInputRef}
                    type="text"
                    className="form-control"
                    placeholder="Search project..."
                    value={projectSearchTerm}
                    onFocus={() => {
                      setShowProjectSuggestions(true);
                      loadProjects();
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setProjectSearchTerm(value);
                      setShowProjectSuggestions(true);
                      searchProjects(value);
                    }}
                    onKeyDown={handleKeyDown}
                  />

                  {showProjectSuggestions && (
                    <div
                      className="dropdown-menu show w-100 p-0 mt-1"
                      style={{
                        maxHeight: "250px",
                        overflowY: "auto",
                      }}
                    >
                      <ul className="list-unstyled mb-0">
                        {filteredProjects.length === 0 &&
                          projectSearchTerm.trim() !== "" && (
                            <li
                              className="suggestion-item text-primary"
                              onMouseDown={handleAddProject}
                            >
                              <span className="badge bg-label-primary p-2">
                                <AddIcon iClass={"me-1"}/>"{projectSearchTerm}"
                              </span>
                            </li>
                          )}

                        {filteredProjects.map((project, index) => (
                          <li
                            key={project._id}
                            ref={(el) => (projectItemRefs.current[index] = el)}
                            className={`suggestion-item ${
                              index === projectIndex
                                ? "highlighted rounded"
                                : ""
                            }`}
                            onMouseDown={() => handleSelectProject(project)}
                          >
                            {project.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <AddProject
          isActive={isActive}
          onClose={closeEditModal}
          setProjects={setProjects}
          initialTitle={newProjectName}
          editData={null}
          setBtnLoading={setBtnLoading}
          isBtnLoading={isBtnLoading}
          onProjectCreated={handleSelectProject}
        />
      )}
    </div>
  );
};
export default ProjectSale;
